import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import BottomNav from '../../components/BottomNav'
import { useAuth } from '../../context/AuthContext'
import { getDashboard } from '../../api/dashboard'
import { getSettings } from '../../api/settings'

const G = '#C89B4C'
const GB = 'rgba(200,155,76,0.14)'
const BD = 'rgba(244,238,225,0.14)'
const S = 'linear-gradient(155deg, rgba(255,248,235,0.05) 0%, rgba(255,248,235,0.015) 100%)'
const GLASS = { background: S, backdropFilter: 'blur(11px) saturate(150%)', WebkitBackdropFilter: 'blur(11px) saturate(150%)' } as const
const TX = '#F4F1EA'
const TX2 = '#C8C4BB'
const TX3 = '#8A8480'
const SERIF = "'Newsreader', Georgia, serif"

const TIER_CFG = {
  bronze: { label: 'Bronze', color: '#CD7F32', bg: 'rgba(205,127,50,0.12)', icon: '🥉' },
  silver: { label: 'Silver', color: '#A0A0B0', bg: 'rgba(160,160,176,0.12)', icon: '🥈' },
  gold:   { label: 'Gold',   color: '#C89B4C', bg: 'rgba(200,155,76,0.14)', icon: '🥇' },
}

function MenuItem({ icon, label, sublabel, onClick, danger }: {
  icon: React.ReactNode; label: string; sublabel?: string; onClick?: () => void; danger?: boolean
}) {
  return (
    <button
      onClick={onClick}
      style={{
        width: '100%', display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px',
        background: 'none', border: 'none', borderBottom: `1px solid ${BD}`,
        cursor: onClick ? 'pointer' : 'default', textAlign: 'left',
      }}
    >
      <span style={{ color: danger ? '#E05E5E' : TX3, display: 'flex', flexShrink: 0 }}>{icon}</span>
      <div style={{ flex: 1 }}>
        <p style={{ margin: 0, fontSize: 13, color: danger ? '#E05E5E' : TX }}>{label}</p>
        {sublabel && <p style={{ margin: '2px 0 0', fontSize: 11, color: TX3 }}>{sublabel}</p>}
      </div>
      {onClick && (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={TX3} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6"/></svg>
      )}
    </button>
  )
}

export default function ProfilePage() {
  const { user, setLanguage, isAdmin } = useAuth()
  const navigate = useNavigate()
  const lang = user?.language ?? 'ru'

  const { data: dash } = useQuery({
    queryKey: ['dashboard'],
    queryFn: getDashboard,
    enabled: !isAdmin,
    retry: false,
  })
  const { data: settings } = useQuery({ queryKey: ['settings'], queryFn: getSettings, staleTime: 60_000 })

  if (!user) return null

  const tier    = (dash?.tier ?? 'bronze') as keyof typeof TIER_CFG
  const tierCfg = TIER_CFG[tier]
  const initial = user.full_name[0]?.toUpperCase() ?? '?'

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <header style={{
        position: 'sticky', top: 0, zIndex: 40, padding: '10px 20px',
        background: 'rgba(13,21,38,0.82)', backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)', borderBottom: `1px solid ${BD}`,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <span style={{ fontSize: 11, fontWeight: 600, color: TX3, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
          {lang === 'uz' ? 'Profil' : 'Профиль'}
        </span>
        <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, fontWeight: 600, color: G, letterSpacing: '0.18em', textTransform: 'uppercase' }}>
          ANSOR NASIYA
        </span>
      </header>

      <main style={{ flex: 1, overflowY: 'auto', paddingBottom: 104 }}>
        {/* Identity hero */}
        <div style={{ padding: '28px 20px 20px', borderBottom: `1px solid ${BD}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            {/* Avatar */}
            <div style={{
              width: 64, height: 64, borderRadius: '50%', flexShrink: 0,
              background: GB, border: `2px solid ${G}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontFamily: SERIF, fontStyle: 'italic', fontSize: 28, color: G,
            }}>
              {initial}
            </div>
            <div style={{ flex: 1 }}>
              <h2 style={{ fontFamily: SERIF, fontStyle: 'italic', fontSize: 22, color: TX, margin: '0 0 4px', lineHeight: 1.2 }}>
                {user.full_name}
              </h2>
              <p style={{ margin: '0 0 8px', fontSize: 12, color: TX3, fontFamily: "'JetBrains Mono', monospace" }}>
                {user.phone}
              </p>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {/* Tier badge */}
                {dash && (
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                    padding: '3px 10px', borderRadius: 99, fontSize: 11, fontWeight: 600,
                    background: tierCfg.bg, color: tierCfg.color,
                    border: `1px solid ${tierCfg.color}40`,
                  }}>
                    {tierCfg.icon} {tierCfg.label}
                  </span>
                )}
                {/* Verified */}
                {user.is_verified ? (
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                    padding: '3px 10px', borderRadius: 99, fontSize: 11,
                    background: 'rgba(76,175,126,0.12)', color: '#4CAF7E',
                    border: '1px solid rgba(76,175,126,0.25)',
                  }}>
                    ✓ {lang === 'uz' ? 'Tasdiqlangan' : 'Верифицирован'}
                  </span>
                ) : (
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                    padding: '3px 10px', borderRadius: 99, fontSize: 11,
                    background: S, color: TX3, border: `1px solid ${BD}`,
                  }}>
                    {lang === 'uz' ? "Tasdiqlanmagan" : 'Не верифицирован'}
                  </span>
                )}
                {/* Discount */}
                {user.personal_discount > 0 && (
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                    padding: '3px 10px', borderRadius: 99, fontSize: 11,
                    background: GB, color: G, border: `1px solid ${G}40`,
                  }}>
                    −{user.personal_discount}%
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Verification hint */}
          {!user.is_verified && (
            <div style={{ marginTop: 14, padding: '12px 14px', borderRadius: 12, background: 'rgba(200,155,76,0.06)', border: `1px solid ${G}30` }}>
              <p style={{ margin: 0, fontSize: 12, color: TX2, lineHeight: 1.55 }}>
                {user.passport_verified
                  ? (lang === 'uz' ? "Pasport qabul qilindi. Menejer tez orada tasdiqlaydi." : 'Паспорт получен. Менеджер скоро верифицирует аккаунт.')
                  : (lang === 'uz' ? "Hisob tasdiqlash uchun pasport suratini yuboring." : 'Для верификации аккаунта отправьте фото паспорта.')}
              </p>
            </div>
          )}
        </div>

        {/* Credit summary (clients only) */}
        {dash && !isAdmin && (
          <div style={{ padding: '16px 20px', borderBottom: `1px solid ${BD}`, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div style={{ padding: '12px 14px', borderRadius: 12, background: S, border: `1px solid ${BD}` }}>
              <p style={{ margin: '0 0 4px', fontSize: 10, color: TX3, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                {lang === 'uz' ? 'Kredit limiti' : 'Кредит. лимит'}
              </p>
              <p style={{ margin: 0, fontFamily: "'JetBrains Mono', monospace", fontSize: 18, color: G, fontWeight: 600 }}>
                ${dash.credit_limit.toLocaleString()}
              </p>
            </div>
            <div style={{ padding: '12px 14px', borderRadius: 12, background: S, border: `1px solid ${BD}` }}>
              <p style={{ margin: '0 0 4px', fontSize: 10, color: TX3, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                {lang === 'uz' ? 'Ishlatilgan' : 'Использовано'}
              </p>
              <p style={{ margin: 0, fontFamily: "'JetBrains Mono', monospace", fontSize: 18, color: TX, fontWeight: 600 }}>
                ${dash.credit_used.toLocaleString()}
              </p>
            </div>
          </div>
        )}

        {/* Language switcher */}
        <div style={{ padding: '16px 20px', borderBottom: `1px solid ${BD}` }}>
          <p style={{ margin: '0 0 12px', fontSize: 10, fontWeight: 600, color: TX3, letterSpacing: '0.10em', textTransform: 'uppercase' }}>
            {lang === 'uz' ? 'TIL' : 'ЯЗЫК'}
          </p>
          <div style={{ display: 'flex', gap: 10 }}>
            {(['ru', 'uz'] as const).map(l => (
              <button
                key={l}
                onClick={() => setLanguage(l)}
                style={{
                  flex: 1, padding: '12px 0', borderRadius: 12, fontWeight: 500, fontSize: 13, cursor: 'pointer',
                  border: `1px solid ${lang === l ? G : BD}`,
                  background: lang === l ? GB : S,
                  color: lang === l ? G : TX3,
                }}
              >
                {l === 'ru' ? '🇷🇺 Русский' : '🇺🇿 O\'zbek'}
              </button>
            ))}
          </div>
        </div>

        {/* Menu items */}
        <div style={{ padding: '8px 20px' }}>
          <p style={{ margin: '12px 0 8px', fontSize: 10, fontWeight: 600, color: TX3, letterSpacing: '0.10em', textTransform: 'uppercase' }}>
            {lang === 'uz' ? 'HISOBIM' : 'МОЙ АККАУНТ'}
          </p>
          <div style={{ borderRadius: 14, overflow: 'hidden', border: `1px solid ${BD}`, ...GLASS }}>
            <MenuItem
              icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="5" width="20" height="14" rx="2"/><path d="M2 10h20"/></svg>}
              label={lang === 'uz' ? "To'lovlar tarixi" : 'История платежей'}
              sublabel={lang === 'uz' ? "Barcha tasdiqlangan to'lovlar" : 'Все подтверждённые платежи'}
              onClick={() => navigate('/payments')}
            />
            <MenuItem
              icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>}
              label={lang === 'uz' ? 'Mening arizalarim' : 'Мои заявки'}
              onClick={() => navigate('/orders')}
            />
            <MenuItem
              icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.07 4.93a10 10 0 010 14.14M4.93 4.93a10 10 0 000 14.14"/></svg>}
              label={lang === 'uz' ? 'Yangi ariza' : 'Новая заявка'}
              onClick={() => navigate('/custom-order')}
            />
          </div>

          {/* Admin badge */}
          {isAdmin && (
            <div style={{ marginTop: 20, padding: '14px 16px', borderRadius: 14, ...GLASS, border: `1px solid ${BD}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontSize: 13, color: TX2 }}>{lang === 'uz' ? 'Rol' : 'Роль'}</span>
              <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12, color: G, fontWeight: 600 }}>
                {user.role.toUpperCase()}
              </span>
            </div>
          )}

          {/* Support */}
          <p style={{ margin: '24px 0 8px', fontSize: 10, fontWeight: 600, color: TX3, letterSpacing: '0.10em', textTransform: 'uppercase' }}>
            {lang === 'uz' ? 'YORDAM' : 'ПОДДЕРЖКА'}
          </p>
          <div style={{ borderRadius: 14, overflow: 'hidden', border: `1px solid ${BD}`, ...GLASS }}>
            <MenuItem
              icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>}
              label={lang === 'uz' ? 'Yordam xizmati' : 'Служба поддержки'}
              sublabel={lang === 'uz' ? 'Telegram orqali bog\'laning' : 'Связаться через Telegram'}
              onClick={() => window.open(`https://t.me/${settings?.support_username ?? 'ansor22_support'}`, '_blank')}
            />
          </div>

          {/* App info */}
          <p style={{ margin: '24px 0 0', textAlign: 'center', fontSize: 10, color: TX3, letterSpacing: '0.06em' }}>
            ANSOR NASIYA · v1.0
          </p>
          <p style={{ margin: '4px 0 0', textAlign: 'center', fontSize: 10, color: TX3 }}>
            ansor22.uz
          </p>
        </div>
      </main>

      <BottomNav />
    </div>
  )
}
